// ============================================================
// Form versioning
// IPLP_FRAMEWORK is the newest (current) version. Each older version
// records how it DIFFERED from current, so a response authored under an
// old version always renders the question text as it read at the time —
// even after many revisions.
// ============================================================
import { IPLP_FRAMEWORK } from './framework'
import type { FrameworkComponent, FormStructure } from './types'

export interface FormVersionChange {
  title?: string
  elements?: string[]
  levels?: Partial<FrameworkComponent['levels']>
}

export interface FormVersion {
  version: number
  label: string
  effective: string
  published: string
  author: string
  note: string
  changes: Record<string, FormVersionChange>
}

// Newest → oldest. `changes` describe how that version differed from current.
export const FORM_VERSIONS: FormVersion[] = [
  {
    version: 3,
    label: 'v3',
    effective: '2026–27',
    published: 'Jun 12, 2026',
    author: 'Maren Holloway',
    note: 'Wording cleanup — corrected capitalization on 2e and 4d.',
    changes: {},
  },
  {
    version: 2,
    label: 'v2',
    effective: '2025–26',
    published: 'Jul 28, 2025',
    author: 'District Office',
    note: 'Title fixes pending; rubric text unchanged.',
    changes: {
      '2e': { title: 'Organizing physical space' },
      '4d': { title: 'Participating in the Professional community' },
    },
  },
  {
    version: 1,
    label: 'v1',
    effective: '2024–25',
    published: 'Aug 9, 2024',
    author: 'District Office',
    note: 'Initial Danielson adoption.',
    changes: {
      '2e': { title: 'Organizing physical space' },
      '4d': { title: 'Participating in the Professional community' },
      '1d': { title: 'Demonstrating Knowledge of Resources and Technology' },
    },
  },
]

const YEAR_VERSION: Record<string, number> = {
  '2026-2027': 3,
  '2025-2026': 2,
  '2024-2025': 1,
  '2023-2024': 1,
}

export const CURRENT_VERSION = 3

export function formVersionForYear(year: string): number {
  return YEAR_VERSION[year] ?? CURRENT_VERSION
}

export function versionMeta(version: number): FormVersion {
  return FORM_VERSIONS.find((v) => v.version === version) ?? FORM_VERSIONS[0]
}

const ALL_COMPONENTS = IPLP_FRAMEWORK.flatMap((d) => d.components)

/**
 * A component's phrasing as captured in a stored form structure
 * (form_templates.structure or an immutable form_template_versions row).
 * Returns undefined when the structure doesn't carry that component, so
 * callers can fall back to the static Danielson catalogue.
 */
export function componentFromStructure(
  structure: FormStructure | null | undefined,
  code: string
): FrameworkComponent | undefined {
  const c = structure?.domains?.flatMap((d) => d.components).find((x) => x.code === code)
  if (!c) return undefined
  return {
    code: c.code,
    title: c.title,
    elements: c.elements,
    levels: c.levels,
    evidenceSeed: ALL_COMPONENTS.find((b) => b.code === code)?.evidenceSeed ?? '',
  }
}

/** A component's text as it read under a given version. */
export function componentAsOf(
  code: string,
  version: number
): (FrameworkComponent & { _versioned?: boolean }) | undefined {
  const base = ALL_COMPONENTS.find((c) => c.code === code)
  if (!base) return undefined
  const ver = FORM_VERSIONS.find((v) => v.version === version)
  const ch = ver?.changes[code]
  if (!ch) return base
  return {
    ...base,
    title: ch.title ?? base.title,
    elements: ch.elements ?? base.elements,
    levels: ch.levels ? { ...base.levels, ...ch.levels } : base.levels,
    _versioned: true,
  }
}
